import { createFontFamily, createImmiTheme, grey } from "@imminently/react/ui/themes/imminently";
import { ThemeProvider } from "@mui/material/styles";
import type { Color, PaletteOptions, Shadows, ThemeOptions } from "@mui/material";

import { ImmiTheme } from "@imminently/react/ui/themes/imminently";
import { FC, PropsWithChildren } from "react";

import "@fontsource/baloo-2";
import "@fontsource/baloo-2/700.css";
import "@fontsource/lato";
import "@fontsource/lato/700.css";
import { Interpolation, Theme } from "@emotion/react";
import { TypographyOptions } from "@mui/material/styles/createTypography";
import { Icon } from "@/components/Icon";
import { scrollable } from "@/components/scrollable";

// ### Define your color palettes! ###
export const royalblue: Color = {
  25: "#FAFBFF",
  50: "#F0F3FF",
  100: "#C3C8DE",
  200: "#A3AAC7",
  300: "#7982A9",
  400: "#5D6791",
  500: "#354176",
  600: "#303B6B",
  700: "#262E54",
  800: "#1D2441",
  900: "#161B32",
  A100: "#ffffff",
  A200: "#ffffff",
  A400: "#0000ff",
  A700: "#ff00ff",
  "contrastDefaultColor": "light",
};

export const teal: Color = {
  25: "#F9FDFD",
  50: "#ECF5F5",
  100: "#C5DFDF",
  200: "#A9CFD0",
  300: "#81B9BA",
  400: "#69ACAD",
  500: "#439798",
  600: "#3D898A",
  700: "#306B6C",
  800: "#255354",
  900: "#1C3F40",
  A100: "#ffffff",
  A200: "#ffffff",
  A400: "#0000ff",
  A700: "#ff00ff",
  "contrastDefaultColor": "light",
};

export const green: Color = {
  25: "#F6FDFB",
  50: "#E6F6F1",
  100: "#B0E2D3",
  200: "#8AD3BE",
  300: "#54BFA0",
  400: "#33B38D",
  500: "#00A071",
  600: "#009267",
  700: "#007250",
  800: "#00583E",
  900: "#00432F",
  A100: "#ffffff",
  A200: "#ffffff",
  A400: "#0000ff",
  A700: "#ff00ff",
  "contrastDefaultColor": "light",
};

export const red: Color = {
  25: "#FFFAFA",
  50: "#FFEDEC",
  100: "#FFC7C4",
  200: "#FFACA8",
  300: "#FF8680",
  400: "#FF6F68",
  500: "#FF4B42",
  600: "#E8443C",
  700: "#B5352F",
  800: "#8C2924",
  900: "#6B201C",
  A100: "#ffffff",
  A200: "#ffffff",
  A400: "#0000ff",
  A700: "#ff00ff",
  "contrastDefaultColor": "light",
};

export const orange: Color = {
  25: "#FFF9F5",
  50: "#FEF1E8",
  100: "#FCD3B8",
  200: "#FABE96",
  300: "#F8A066",
  400: "#F68D48",
  500: "#F4711A",
  600: "#DE6718",
  700: "#AD5012",
  800: "#863E0E",
  900: "#662F0B",
  A100: "#ffffff",
  A200: "#ffffff",
  A400: "#0000ff",
  A700: "#ff00ff",
  "contrastDefaultColor": "light",
};

export const blue: Color = {
  25: "#F8FCFF",
  50: "#E8F5FE",
  100: "#B8DEFC",
  200: "#96CFFA",
  300: "#66B9F8",
  400: "#48ABF6",
  500: "#1A96F4",
  600: "#1889DE",
  700: "#126BAD",
  800: "#0E5386",
  900: "#0B3F66",
  A100: "#ffffff",
  A200: "#ffffff",
  A400: "#0000ff",
  A700: "#ff00ff",
  "contrastDefaultColor": "light",
};

export const yellow: Color = {
  25: "#FFFDF9",
  50: "#FEF6E8",
  100: "#FBE4B7",
  200: "#F9D795",
  300: "#F6C564",
  400: "#F5BA46",
  500: "#F2A918",
  600: "#DC9A16",
  700: "#AC7811",
  800: "#855D0D",
  900: "#66470A",
  A100: "#ffffff",
  A200: "#ffffff",
  A400: "#0000ff",
  A700: "#ff00ff",
  "contrastDefaultColor": "light",
};

export const purple: Color = {
  25: "#FEFAFF",
  50: "#F5E8FE",
  100: "#E1B8FC",
  200: "#D296FA",
  300: "#BD66F8",
  400: "#B148F6",
  500: "#9D1AF4",
  600: "#8F18DE",
  700: "#6F12AD",
  800: "#560E86",
  900: "#420B66",
  A100: "#ffffff",
  A200: "#ffffff",
  A400: "#0000ff",
  A700: "#ff00ff",
  "contrastDefaultColor": "light",
};

// ### Create custom palette! ###

const palette: PaletteOptions = {
  mode: "light",
  primary: royalblue,
  secondary: teal,
  error: {
    light: red[300],
    main: red[500],
    dark: red[700],
    contrastText: "#fff",
    ...red,
  },
  success: {
    light: green[300],
    main: green[500],
    dark: green[700],
    contrastText: "#fff",
    ...green,
  },
  warning: {
    light: yellow[300],
    main: yellow[500],
    dark: yellow[700],
    contrastText: "#fff",
    ...yellow,
  },
  text: {
    primary: royalblue[900],
    secondary: royalblue[900],
    disabled: grey[900]
  },
  common: {
    green,
    red,
    yellow,
    orange,
    blue,
    purple
  }
};

const components: ThemeOptions["components"] = {
  // override mui components / theme with project specifics
  MuiTypography: {
    defaultProps: {
      // map typography variants to html tags
      variantMapping: {
        bodyLarge: "p",
        buttonSmall: "p",
        buttonLarge: "p",
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        padding: "0.5rem 1rem",
        height: "2.5rem",
      },
      sizeSmall: {
        padding: "0.375rem 0.5rem",
        height: "2rem", // design wants 28px?? no
      },
      sizeLarge: {
        padding: "0.75rem 1.5rem",
        height: "3rem",
      },
      containedSecondary: {
        backgroundColor: royalblue[500],
      },
      outlined: {
        borderWidth: "2px !important",

      },
    }
  },
  MuiToolbar: {
    styleOverrides: {
      root: {
        // needs to override a min width media query
        padding: "0 2rem 0 0.75rem !important"
      }
    }
  },
  MuiBreadcrumbs: {
    styleOverrides: {
      separator: {
        margin: 0,
      }
    }
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: royalblue[700],
        color: "#fff",
        fontSize: "1rem",
        padding: "0.5rem 1rem",
      },
      arrow: {
        color: royalblue[700],
      }
    }
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        "& > .MuiButton-startIcon": {
          marginLeft: 0,
          marginRight: "0.5rem",
        },
      },
    }
  },
  MuiFormControlLabel: {
    defaultProps: {
      labelPlacement: "top",
      sx: { alignItems: "flex-start", m: 0, gap: 1 },
      componentsProps: { typography: { sx: { fontWeight: "bold" } } }
    }
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        backgroundColor: "#C3C8DE",
        borderRadius: "1.25rem",
        border: "4px solid #C3C8DE",
        padding: 0,
        gap: "0.25rem",

        "& .MuiButtonBase-root": {
          color: "#161B32",
          borderRadius: "1rem !important",
          border: "none",
          flex: 1,

          // doubled to increase specificity
          "&.Mui-selected.Mui-selected": {
            color: "#161B32",
            backgroundColor: royalblue[25],
            "&:hover": {
              backgroundColor: royalblue[25],
            }
          },

          "&:hover": {
            backgroundColor: "unset",
            color: royalblue[25],
          }
        },

      }
    },
  },
  MuiSelect: {
    styleOverrides: {
      outlined: {
        padding: "0.5rem 1rem",
      }
    },
    defaultProps: {
      MenuProps: {
        MenuListProps: { dense: true },
        PaperProps: { sx: { mt: 0.5, maxHeight: "calc(3rem * 5 + 1.5rem)" }, className: scrollable }
      },
      IconComponent: (props: any) => <Icon icon="chevrondown" {...props} style={{
        right: "0.875rem",
        top: "calc(50% - 0.75rem)",
        color: "inherit",
      }} />,
    }
  },
  MuiButtonGroup: {
    defaultProps: {
      disableElevation: true,
    },
  },
  MuiDialog: {
    styleOverrides: {
      paperFullScreen: {
        width: "calc(100% - 2rem)",
        height: "calc(100% - 2rem)",
        borderRadius: "1rem",
      }
    }
  }
};

const shape = { borderRadius: 8 };
const lato = createFontFamily("Lato");
const baloo = createFontFamily("\"Baloo 2\"");
const shadow = "0px 2px 8px 0px rgba(53, 65, 118, 0.56)";
const shadows = [...Array(25).keys()].map((i) => i === 0 ? "none" : shadow) as Shadows;

const typography: TypographyOptions = {
  // main font is lato
  fontFamily: lato,
  // h1-h6 are baloo
  h1: { fontFamily: baloo, fontSize: "2.5rem" },
  h2: { fontFamily: baloo, fontSize: "2rem" },
  h3: { fontFamily: baloo, fontSize: "1.75rem" },
  h4: { fontFamily: baloo, fontSize: "1.5rem" },
  h5: { fontFamily: baloo, fontSize: "1.25rem" },
  h6: { fontFamily: baloo, fontSize: "1rem", textTransform: "none" },
  // h7 in designs
  subtitle1: { fontFamily: baloo, fontSize: "0.75rem", fontWeight: 700, lineHeight: 1.2, textTransform: "uppercase" },
  body1: { fontFamily: lato, fontSize: "1rem", fontWeight: 400, lineHeight: 1.5 },
  body2: { fontFamily: lato, fontSize: "0.875rem", fontWeight: 400, lineHeight: 1.5 },
  bodyLarge: { fontFamily: lato, fontSize: "1.25rem", fontWeight: 400, lineHeight: 1.5 },
  caption: { fontFamily: lato, fontSize: "0.75rem", fontWeight: 400, lineHeight: 1.5 },
  // line height of 1 for button text is kind of annoying, will set height on the buttons
  button: { fontFamily: lato, fontSize: "1rem", fontWeight: 400, lineHeight: 1 },
  buttonSmall: { fontFamily: lato, fontSize: "0.75rem", fontWeight: 400, lineHeight: 1 },
  buttonLarge: { fontFamily: lato, fontSize: "1.25rem", fontWeight: 400, lineHeight: 1 },
};

const appTheme = createImmiTheme({ palette, typography, components, shape, shadows });

export const AppThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  return (
    <ThemeProvider theme={appTheme}>
      {children}
    </ThemeProvider>
  );
};

export const getTheme = (p: any) => p.theme as ImmiTheme;

// re-export theme for easy reference
export { css } from "@emotion/react";
export type { Theme } from "@emotion/react";

export type CssProp = Interpolation<Theme>;